import { downloadPDFsAsZip, generatePDF } from '../../../utils/generatePDF';
import { collectionDispatch } from './collectionDispatch';

const sanitizeFileNamePart = (value) =>
  (value || '').replace(/[/\\:*?"<>|]/g, '-').trim();

/**
 * Same idsField(s) -> populate value the show page's table
 * (useMailSenderShowParameters) uses for this document_type, e.g. "owners"
 * or "veterinarians,groomers".
 *
 * @function
 * @param {string} type - mail-sender document_type (route param).
 * @returns {string} The `populate` query param value, or '' if none.
 */
const getPopulateParam = (type) => {
  const config = collectionDispatch(type);
  const idsFields = (config.multi || [config])
    .map((c) => c.idsField)
    .filter(Boolean);
  return idsFields.join(',');
};

/**
 * Fetches the real entities (with their display name) behind a mail-sender's
 * document list, reusing the same `mail-sender/show/:type/:id` endpoint the
 * show page's table already uses.
 *
 * @function
 * @param {Object} params - The parameters for resolving entity names.
 * @param {Function} params.dispatchAPI - Function for dispatching API requests.
 * @param {string} params.type - mail-sender document_type (route param).
 * @param {string} params.id - mail-sender id.
 * @param {Array} params.items - The download items to resolve names for.
 * @returns {Promise<Object>} A map of entity _id to display name.
 */
const getEntityNames = async ({ dispatchAPI, type, id, items }) => {
  const populate = getPopulateParam(type);
  const { data } = await dispatchAPI('GET', {
    url: `/mail-sender/show/${type}/${id}?is_archived!=true&limit=${
      items.length
    }&skip=0${populate ? `&populate=${populate}` : ''}`
  });

  const nameById = {};
  (data || []).forEach((entity) => {
    nameById[entity._id] =
      entity.name ||
      `${entity.last_name || ''} ${entity.first_name || ''}`.trim();
  });
  return nameById;
};

/**
 * Builds a unique, filesystem-safe "Label_Entity name.pdf" file name per item.
 *
 * @function
 * @param {Object} params - The parameters for building file names.
 * @param {string} params.label - Display label shared by every file (the document_type tag).
 * @param {Array} params.items - The download items to name.
 * @param {Object} params.nameById - Map of entity _id to display name.
 * @returns {string[]} File names, in the same order as `items`.
 */
const buildFileNames = ({ label, items, nameById }) => {
  const safeLabel = sanitizeFileNamePart(label);
  const seen = new Map();

  return items.map((item) => {
    const entityName = sanitizeFileNamePart(
      nameById[item.documentId] || item.documentId
    );
    const baseName = `${safeLabel}_${entityName}`;
    const count = (seen.get(baseName) || 0) + 1;
    seen.set(baseName, count);
    return `${count > 1 ? `${baseName} (${count})` : baseName}.pdf`;
  });
};

/**
 * Downloads the document(s) for a mail-sender's real entities via
 * TemplateDocumentButton's endpoint (generatePDF): a single named PDF for one
 * item, or a zip of named PDFs for several.
 *
 * @function
 * @param {Object} params - The parameters for downloading the document(s).
 * @param {Function} params.dispatchAPI - Function for dispatching API requests.
 * @param {Function} params.message - Function for displaying error messages.
 * @param {Function} params.t - Translation function.
 * @param {Function} params.setLoading - Function to toggle the page's loading modal.
 * @param {string} params.type - mail-sender document_type (route param).
 * @param {string} params.id - mail-sender id.
 * @param {Array<{collection: string, documentId: string, useCase: string}>} params.items - Items to download.
 * @returns {Promise<void>} Resolves once the download has been triggered.
 */
export const downloadTemplateDocuments = async ({
  dispatchAPI,
  message,
  t,
  setLoading,
  type,
  id,
  items
}) => {
  if (!items.length) return;

  setLoading(true);
  try {
    const nameById = await getEntityNames({ dispatchAPI, type, id, items });
    const label = t(`mail-sender.tags.${type}`);
    const fileNames = buildFileNames({ label, items, nameById });

    if (items.length === 1) {
      await generatePDF({
        message,
        dispatchAPI,
        parameters: [
          {
            collection: items[0].collection,
            documentId: items[0].documentId
          }
        ],
        use_case: items[0].useCase,
        downloadName: fileNames[0].replace(/\.pdf$/, '')
      });
      return;
    }

    const blobs = await Promise.all(
      items.map((item) =>
        generatePDF({
          message,
          dispatchAPI,
          parameters: [
            { collection: item.collection, documentId: item.documentId }
          ],
          use_case: item.useCase,
          printPdf: false,
          downloadName: fileNames[0].replace(/\.pdf$/, '')
        })
      )
    );

    const validPairs = blobs
      .map((blob, index) => ({ blob, fileName: fileNames[index] }))
      .filter(({ blob }) => blob instanceof Blob);

    await downloadPDFsAsZip(
      validPairs.map((pair) => pair.blob),
      `${sanitizeFileNamePart(label)}.zip`,
      validPairs.map((pair) => pair.fileName)
    );
  } finally {
    setLoading(false);
  }
};
